/**
 * Eric / Vincent (2026-05-14, bug #124) — Robust API error stringifier.
 *
 * Centralised because too many places do `toast.error(err.response?.data?.detail || err.message)`
 * which silently produces "[object Object]" when FastAPI returns structured
 * validation errors (`detail: [{loc, msg, type}]`) or Pydantic error objects.
 *
 * This helper accepts any axios error (or a raw object) and always returns a
 * human-friendly string suitable for a toast.
 */

export function apiErrorMessage(err, fallback = "Erreur") {
  if (!err) return fallback;
  // axios error shape: err.response?.data?.detail (FastAPI), err.response?.data?.message, err.message
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    // FastAPI validation error: list of {loc, msg, type}. Join the msg fields.
    const msgs = detail
      .map((d) => {
        if (typeof d === "string") return d;
        if (d && typeof d === "object") {
          const loc = Array.isArray(d.loc) ? d.loc.join(".") : (d.loc || "");
          return loc ? `${loc}: ${d.msg || JSON.stringify(d)}` : (d.msg || JSON.stringify(d));
        }
        return String(d);
      })
      .filter(Boolean);
    if (msgs.length > 0) return msgs.join(" · ");
  }
  if (detail && typeof detail === "object") {
    // Single object with .msg / .message / .error field.
    if (detail.msg) return String(detail.msg);
    if (detail.message) return String(detail.message);
    if (detail.error) return String(detail.error);
    try {
      const s = JSON.stringify(detail);
      return s.length > 240 ? s.slice(0, 240) + "…" : s;
    } catch { /* fallthrough */ }
  }
  // Other axios fields
  const dataMsg = err?.response?.data?.message;
  if (typeof dataMsg === "string" && dataMsg.trim()) return dataMsg;
  if (typeof err.message === "string" && err.message.trim()) return err.message;
  return fallback;
}
